import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ShieldCheckIcon, DevicePhoneMobileIcon, EnvelopeIcon, KeyIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { mfaApi } from '@/services/mfaApi';

type SetupFlow = 'totp' | 'email' | null;

const BackupCodesPanel: React.FC<{ codes: string[]; onDone: () => void }> = ({ codes, onDone }) => (
  <div className="mt-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
    <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
      Save these backup codes now — each works once, and this is the only time they're shown.
    </p>
    <div className="grid grid-cols-2 gap-2 font-mono text-sm text-gray-900 dark:text-white mb-3">
      {codes.map((code) => (
        <div key={code} className="bg-white dark:bg-gray-800 rounded px-2 py-1 border border-gray-200 dark:border-gray-700">
          {code}
        </div>
      ))}
    </div>
    <button
      onClick={onDone}
      className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md"
    >
      I've saved these codes
    </button>
  </div>
);

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [setupFlow, setSetupFlow] = useState<SetupFlow>(null);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [busy, setBusy] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ['mfa-status'],
    queryFn: () => mfaApi.getStatus(),
  });

  const invalidateStatus = () => queryClient.invalidateQueries({ queryKey: ['mfa-status'] });

  const startTotpSetup = async () => {
    setBusy(true);
    try {
      const { secret, qrCodeDataUrl } = await mfaApi.setupTotp();
      setTotpSecret(secret);
      setQrCodeDataUrl(qrCodeDataUrl);
      setSetupFlow('totp');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to start authenticator setup');
    } finally {
      setBusy(false);
    }
  };

  const confirmTotpSetup = async () => {
    setBusy(true);
    try {
      const { backupCodes } = await mfaApi.confirmTotp(totpCode);
      setBackupCodes(backupCodes);
      toast.success('Authenticator app MFA enabled');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Incorrect code — check your authenticator app and try again.');
    } finally {
      setBusy(false);
    }
  };

  const enableEmailMfa = async () => {
    setBusy(true);
    try {
      const { backupCodes } = await mfaApi.enableEmail();
      setBackupCodes(backupCodes);
      setSetupFlow('email');
      toast.success('Email code MFA enabled');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to enable email MFA');
    } finally {
      setBusy(false);
    }
  };

  const finishSetup = () => {
    setSetupFlow(null);
    setTotpSecret(null);
    setQrCodeDataUrl(null);
    setTotpCode('');
    setBackupCodes(null);
    invalidateStatus();
  };

  const disableMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await mfaApi.disable(disablePassword);
      toast.success('MFA disabled');
      setShowDisableForm(false);
      setDisablePassword('');
      invalidateStatus();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Incorrect password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Profile</h1>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Account</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">{user?.name} &middot; {user?.email}</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Two-factor authentication</h2>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Add a second step when signing in, on top of your password.
        </p>

        {isLoading ? (
          <div className="h-16 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
        ) : backupCodes ? (
          <BackupCodesPanel codes={backupCodes} onDone={finishSetup} />
        ) : status?.mfaEnabled ? (
          <div>
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400 mb-1">
              <ShieldCheckIcon className="h-4 w-4" />
              Enabled via {status.mfaMethod === 'totp' ? 'authenticator app' : 'email code'}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {status.backupCodesRemaining} backup code{status.backupCodesRemaining === 1 ? '' : 's'} remaining.
            </p>

            {showDisableForm ? (
              <form onSubmit={disableMfa} className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Confirm your password to disable MFA
                  </label>
                  <input
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    required
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                >
                  Disable
                </button>
                <button
                  type="button"
                  onClick={() => { setShowDisableForm(false); setDisablePassword(''); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md"
                >
                  Cancel
                </button>
              </form>
            ) : (
              <button
                onClick={() => setShowDisableForm(true)}
                className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                Disable MFA
              </button>
            )}
          </div>
        ) : setupFlow === 'totp' ? (
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
              Scan this with Google Authenticator, Authy, or 1Password, then enter the 6-digit code it shows.
            </p>
            {qrCodeDataUrl && (
              <img src={qrCodeDataUrl} alt="Authenticator QR code" className="mb-3 rounded border border-gray-200 dark:border-gray-700" />
            )}
            {totpSecret && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-3 font-mono break-all">
                Can't scan? Enter this key manually: {totpSecret}
              </p>
            )}
            <div className="flex items-end gap-2">
              <input
                type="text"
                inputMode="numeric"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                placeholder="6-digit code"
                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
              />
              <button
                onClick={confirmTotpSetup}
                disabled={busy || !totpCode}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
              >
                Confirm
              </button>
              <button
                onClick={() => { setSetupFlow(null); setTotpSecret(null); setQrCodeDataUrl(null); setTotpCode(''); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            <button
              onClick={startTotpSetup}
              disabled={busy}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              <DevicePhoneMobileIcon className="h-4 w-4" />
              Set up authenticator app
            </button>
            <button
              onClick={enableEmailMfa}
              disabled={busy}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <EnvelopeIcon className="h-4 w-4" />
              Use email codes instead
            </button>
          </div>
        )}

        {status?.mfaEnabled && (
          <p className="mt-4 flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
            <KeyIcon className="h-3.5 w-3.5" />
            Lost your device or email access? A backup code works in place of the regular code at sign-in.
          </p>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
